import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { Suspense } from 'preact/compat';

const FocusLock: FunctionalComponent = ({ children, ...otherProps }) => {
  const rootNode = useRef<HTMLDivElement>(null);
  const focusableItems = useRef<HTMLElement[]>([]);

  const FOCUSABLE_ELEMENTS_SELECTOR = [
    'a[href]:not([tabindex="-1"])',
    'button:not([disabled]):not([tabindex="-1"])',
    'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
    'select:not([disabled]):not([tabindex="-1"])',
    'textarea:not([disabled]):not([tabindex="-1"])',
    '[contenteditable]:not([tabindex="-1"])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  // To check for focusable items
  useEffect(() => {
    const updateFocusableItems = () => {
      if (rootNode?.current != null) {
        focusableItems.current = Array.from(rootNode.current.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR));
      }
    };

    const observer = new MutationObserver(() => {
      updateFocusableItems();
    });

    updateFocusableItems();

    if (rootNode?.current != null) {
      observer.observe(rootNode.current, { childList: true });
    }

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootNode.current]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      const { code, shiftKey } = event;

      if (code === 'Tab') {
        if (rootNode?.current != null) {
          focusableItems.current = Array.from(rootNode.current.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR));
        }

        const activeElement = document.activeElement?.shadowRoot?.querySelector(':focus-visible');

        if (!focusableItems.current) {
          return;
        }

        const { length, 0: firstItem, [length - 1]: lastItem } = focusableItems.current;

        // If only one item then prevent tabbing when locked
        if (length === 1) {
          event.preventDefault();

          return;
        }

        // eslint-disable-next-line no-undefined
        if (activeElement === undefined) {
          event.preventDefault();
          firstItem.focus();
        }

        // If focused on last item then focus on first item when tab is pressed
        if (!shiftKey && activeElement === lastItem) {
          event.preventDefault();
          firstItem.focus();

          return;
        }

        // If focused on first item then focus on last item when shift + tab is pressed
        if (shiftKey && activeElement === firstItem) {
          event.preventDefault();
          lastItem.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusableItems]);

  return (
    <Suspense fallback={<Fragment />}>
      <div id="focus-lock-id" {...otherProps} ref={rootNode}>
        {children}
      </div>
    </Suspense>
  );
};

export default FocusLock;
